import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AppGlyphComponent, AppGlyphKey } from '../../../../shared/components/app-glyph/app-glyph.component';

/**
 * The application (or Administration group) a masthead describes. The
 * container (`app-masthead.container.ts`) maps an `AppDefinition` or an
 * `AdminGroupDefinition` into this; the masthead itself knows nothing about
 * registries, routes or roles.
 */
export interface AppMastheadApp {
  /** Which glyph to draw in the tile. */
  readonly key: AppGlyphKey;
  /** The app name, rendered as the page's `h1`. */
  readonly name: string;
  /**
   * One-line purpose shown under the name (hidden on narrow widths). Admin
   * groups carry an {@link eyebrow} instead and leave this out.
   */
  readonly purpose?: string;
  /**
   * Short label shown *above* the name in place of the purpose line, in
   * small letter-spaced capitals ("Administration"). When set, the purpose
   * line is not rendered. Hidden at the same width as the purpose line.
   */
  readonly eyebrow?: string;
}

/**
 * Visual register of the masthead. `app` is the bright, elevated band the
 * four applications use; `admin` is the quieter "backstage" band of the
 * Administration area (docs/design/admin-headers/README.md, concept A2):
 * muted surface with a hairline, dark tile with a light glyph, no shadow.
 * Metrics, tabs and breakpoints are identical in both.
 */
export type AppMastheadTone = 'app' | 'admin';

/** One section tab. `key` identifies the active tab; `link` is the routerLink target. */
export interface AppMastheadTab {
  readonly key: string;
  readonly label: string;
  readonly link: string;
}

/**
 * Container-query widths (px, measured on the `app-masthead` host) that the
 * stylesheet switches at. Kept in sync with the `$masthead-*` variables at the
 * top of `app-masthead.component.scss`; exported so stories and the motif
 * component (story 5) can reference the same numbers.
 *
 * - `motifCompact`: at or below this width the motif slot should render its
 *   compact variant. Story 5 uses `@container masthead (max-width: 1024px)`.
 * - `purposeHidden`: at or below this width the purpose line is hidden and
 *   the tab row loses its indent.
 */
export const APP_MASTHEAD_BREAKPOINTS = Object.freeze({
  motifCompact: 1024,
  purposeHidden: 800,
});

/**
 * **App masthead** — the white band under the breadcrumb that names the
 * current application (glyph tile, name, purpose) and lists its sections as
 * tabs. Presentational: data in via `app` / `tabs` / `activeTabKey`, navigation
 * out through plain `routerLink` anchors.
 *
 * - The `h1` is the page's main heading; pages do not render their own.
 * - One tab is always active. `activeTabKey` decides (not router state); if it
 *   matches no tab the first tab is shown active.
 * - Tabs scroll horizontally (hidden scrollbar) instead of wrapping.
 * - The host is a CSS container named `masthead`; the stylesheet hides the
 *   purpose line (or the eyebrow) and the motif slot can go compact via
 *   `@container masthead (max-width: …)` (see {@link APP_MASTHEAD_BREAKPOINTS}).
 * - `tone` picks the register ({@link AppMastheadTone}); it is reflected on
 *   the host as `data-tone` and, for admin, the `masthead--admin` class. The
 *   tab row's `nav` landmark is labelled "App sections" in the app tone and
 *   "Administration sections" in the admin tone.
 *
 * Slots:
 * - `[masthead-motif]` — decorative artwork, placed in an `aria-hidden`
 *   520×72 box at the top-right, behind the content, pointer-events off.
 * - `[masthead-action]` — an optional action aligned to the right of the tab
 *   row. The masthead only positions it.
 *
 * ```html
 * <app-masthead [app]="app" [tabs]="tabs" [activeTabKey]="'in-progress'">
 *   <app-motif masthead-motif app="om" />
 *   <p-button masthead-action label="New opportunity" [outlined]="true" />
 * </app-masthead>
 *
 * <app-masthead tone="admin" [app]="{ key: 'admin', name: 'Teams', eyebrow: 'Administration' }" [tabs]="tabs" activeTabKey="teams" />
 * ```
 */
@Component({
  standalone: true,
  selector: 'app-masthead',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AppGlyphComponent, RouterLink],
  host: {
    '[attr.data-tone]': 'tone()',
    '[class.masthead--admin]': "tone() === 'admin'",
  },
  template: `
    <header class="masthead">
      <div class="masthead__motif" aria-hidden="true">
        <ng-content select="[masthead-motif]" />
      </div>

      <div class="masthead__identity">
        <span class="masthead__tile">
          <app-glyph [app]="app().key" [size]="24" />
        </span>
        <div class="masthead__text">
          @if (app().eyebrow) {
            <p class="masthead__eyebrow">{{ app().eyebrow }}</p>
          }
          <h1 class="masthead__name">{{ app().name }}</h1>
          @if (!app().eyebrow) {
            <p class="masthead__purpose">{{ app().purpose }}</p>
          }
        </div>
      </div>

      <div class="masthead__row">
        <nav class="masthead__tabs" [attr.aria-label]="navLabel()">
          @for (tab of tabs(); track tab.key) {
            <a
              class="masthead__tab"
              [routerLink]="tab.link"
              [class.masthead__tab--active]="tab.key === resolvedActiveKey()"
              [attr.aria-current]="tab.key === resolvedActiveKey() ? 'page' : null"
            >
              {{ tab.label }}
            </a>
          }
        </nav>
        <div class="masthead__action">
          <ng-content select="[masthead-action]" />
        </div>
      </div>
    </header>
  `,
  styleUrl: './app-masthead.component.scss',
})
export class AppMastheadComponent {
  /** The app to describe: glyph key, name and purpose (or eyebrow). */
  readonly app = input.required<AppMastheadApp>();
  /** Visual register: `app` (default) or `admin`. See {@link AppMastheadTone}. */
  readonly tone = input<AppMastheadTone>('app');
  /** The section tabs, in display order. The first one is the fallback active tab. */
  readonly tabs = input.required<readonly AppMastheadTab[]>();
  /** Key of the active tab. Unknown keys fall back to the first tab. */
  readonly activeTabKey = input.required<string>();

  /** Accessible name of the tab row's `nav` landmark, per tone. */
  readonly navLabel = computed<string>(() => (this.tone() === 'admin' ? 'Administration sections' : 'App sections'));

  /** The tab key actually highlighted: `activeTabKey` when it matches a tab, else the first tab's key. */
  readonly resolvedActiveKey = computed<string | undefined>(() => {
    const tabs = this.tabs();
    const requested = this.activeTabKey();
    return tabs.some((tab) => tab.key === requested) ? requested : tabs[0]?.key;
  });
}
